import { Alert, Button, Container, Icon, Loading, Panel, SearchInput, Spinner, Typography } from "@/components/atoms";
import { GeneralFooter, GeneralHeader } from "@/components/organisms";
import { OperatorEmblem } from "@/components/organisms/SignUp/contents";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useSiteConfig } from "@/hooks";
import { useAttachClientToProviderMutation } from "@/redux/apis/account/accountApi";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useAppSelector } from "@/redux/hooks";
import { Operator } from "@/redux/slices/operator/types";
import { addToast } from "@/utils";
import { useEffect, useState } from "react";
import { Restricted } from "../Restricted/Restricted";
import { useGetClientOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { profileSelector } from "@/redux/slices/profile/selectors";

export const JoinOperator = () => {
  const { apiUrl } = useSiteConfig();
  const profile = useAppSelector(profileSelector);
  const activeOperator = useAppSelector((state) => state.operator.activeOperator);
  const [attachClientToProvider, { isLoading: isAttachingOperator }] = useAttachClientToProviderMutation();
  const { isFetching: isFetchingOperators, data } = useGetClientOperatorsQuery();
  const [searchProvider, { isFetching: isSearchingProvider }] = useLazyVerifyProviderQuery();
  const [isProviderInvalid, setIsProviderInvalid] = useState<boolean>(false);
  const [selectedOperator, setSelectedOperator] = useState<Operator>();
  const [search, setSearch] = useState<string>();
  const isLoading = isFetchingOperators || isAttachingOperator;

  useEffect(() => {
    if (search) {
      searchProvider({ providerCode: search, apiUrl })
        .unwrap()
        .then((operator) => {
          setIsProviderInvalid(false);
          setSelectedOperator(operator);
        })
        .catch(() => {
          setIsProviderInvalid(true);
          setSelectedOperator(undefined);
        });
    } else {
      setIsProviderInvalid(false);
      setSelectedOperator(undefined);
    };
  }, [search, searchProvider, apiUrl]);

  const onSubmit = () => {
    if (selectedOperator) {
      if (data?.data.operators.find(operator => operator.id === selectedOperator.id)) {
        const { isSuspended } = selectedOperator.flags;
        if (isSuspended) addToast("danger", `${selectedOperator.name} is no longer active on RideMinder.`);
        else addToast("danger", `You've been suspended by ${selectedOperator.name}.`);
      } else {
        attachClientToProvider({ clientId: profile.id, providerId: selectedOperator.id })
          .unwrap()
          .then(() => {
            addToast("success", "Successfully connected with operator");
            setTimeout(() => window.location.replace("../passenger/booking/history"), 1250);
          })
          .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
      }
    }
  };

  const renderWarning = () => {
    const previousOperator = data?.data.operators[0];
    if (previousOperator) {
      if (previousOperator.flags.isSuspended) {
        return (
          <Alert 
            type="warning" 
            className="mt-4" 
            message={`${previousOperator.name} is no longer active\n\nThis transport operator is no longer available on RideMinder. You can join another active operator by entering their Provider Code below.`} 
          />
        );
      } else {
        return (
          <Alert 
            type="warning" 
            className="mt-4" 
            message={`You've been suspended by ${previousOperator.name}\n\nYou can apply to join a different transport operator using their Provider Code.\n\nNote: Joining a new operator will fully detach you from ${previousOperator.name}`} 
          />
        );
      }
    } else return null;
  };

  const renderProvider = () => {
    if (isSearchingProvider) {
      return (
        <div className="flex flex-col items-center justify-center pt-8 md:pt-[60px] pb-[30px] px-7 md:px-14 gap-y-4">
          <Spinner className="h-12 w-12" />
          <Typography variant="paragraph">Checking Provider Code...</Typography>
        </div>
      );
    }

    if (isProviderInvalid) {
      return (
        <div className="flex flex-col items-center justify-center pt-8 md:pt-[60px] pb-[30px] px-7 md:px-14 gap-y-4">
          <Icon variant="Bold" name="Danger" className="text-warning" />
          <Typography variant="paragraph" className="text-center">We couldn&apos;t find the Transport Provider Code that you&apos;ve entered. Please contact your provider</Typography>
        </div>
      );
    } else if (selectedOperator) {
      return (
        <div className="flex flex-col items-center justify-center pt-8 md:pt-[60px] pb-[30px] px-7 md:px-14 gap-y-4">
          <OperatorEmblem name={selectedOperator.name} emblem={selectedOperator.images.emblem} />
          <Typography variant="action" className="text-center">{selectedOperator.name}</Typography>
        </div>
      );
    }
  };

  if (activeOperator) return <Restricted />;

  return (
    <div className="flex flex-row min-h-screen overflow-hidden">
      <div className="flex flex-1 flex-col relative">
        <GeneralHeader action="logout" />
          <Container className="flex flex-1 flex-col items-center w-full max-w-[1040px] gap-5 py-8 md:py-[100px] px-5">
            <div className="flex flex-1 flex-col w-full max-w-[510px]">
              <Panel className="relative flex flex-col">
                {isLoading && <Loading />}
                <Typography variant="h1">Join a new operator</Typography>
                <Typography variant="paragraph" className="mt-2">Join your preferred operator</Typography>
                {renderWarning()}
                <Typography variant="h3" className="mt-4">Transport Provider Code</Typography>
                <SearchInput 
                  className="mt-4" 
                  search={search} 
                  onSearch={(value) => setSearch(value)} 
                  placeholder="Enter your Transport Provider Code" 
                  isLoading={isSearchingProvider}
                />
                <Typography variant="small" className="text-neutral-dark-gray mt-1">Use the transport provider code to connect with a specific operator. Simply ask the operator for the code.</Typography>
                {renderProvider()}
              </Panel>
              <div className="flex justify-end mt-6">
                <Button
                  variant="primary"
                  endIcon="ArrowRight"
                  disabled={!selectedOperator || isLoading}
                  onClick={onSubmit}
                  size="lg"
                >
                  Next
                </Button>
              </div>
            </div>
            <div className="flex flex-row max-w-[510px] text-center">
              <Typography variant="small" className="text-neutral-dark-gray">Contact RideMinder on support@rideminder.com to be matched with a professional transport provider in your state</Typography>
            </div>
          </Container>
        <GeneralFooter />
      </div>
    </div>
  );
};
